import { Alerts } from 'constants/alerts';
import { Resources } from 'constants/resources';

import 'pages/Dashboard/config/chart';
import { useApiContext } from 'context/apiContext';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { ListContextProvider, ListControllerResult, useListController } from 'react-admin';
import { Alert } from 'shared/mui/Alert/Alert';
import { Box } from 'shared/mui/Box';
import { DEFAULT_TIMEZONE } from 'shared/mui/DatePicker/config/defaultValues';
import { PageHeader } from 'shared/mui/PageHeader';
import { Stack } from 'shared/mui/Stack/Stack';
import { cleanEmpty } from 'utils/cleanEmpty';
import { getReportPeriod } from 'utils/getReportPeriod';

import { BoxStyled } from './Dashboard.styled';
import { DashboardFilter } from './DashboardFilter/DashboardFilter';
import { DashboardMerchantsSummary } from './DashboardMerchantsSummary/DashboardMerchantsSummary';
import { DashboardPropvidersSummary } from './DashboardProvidersSummary/DashboardProvidersSummary';
import { DashboardSkeleton } from './DashboardSkeleton';
import { DashboardTransactionsDynamicSummary } from './DashboardTransactionsDynamicSummary/DashboardTransactionsDynamicSummary';
import { DashboardTransactionsSummary } from './DashboardTransactionsSummary/DashboardTransactionsSummary';
import { useDashboardData } from './hooks/useDashboardData';

export const Dashboard: React.FC = () => {
  const translate = useTypedTranslate();
  const { user } = useApiContext();
  const isEmptyMerchantAccess = user?.alerts.includes(Alerts.EmptyMerchantAccess);

  const { filterValues, ...restContext } = useListController({
    resource: Resources.Dashboard,
    queryOptions: { enabled: false },
  });
  const isEmptyUrlFilter = Object.keys(cleanEmpty(filterValues)).length === 0;

  const {
    dashboardTransactionsSuccessSummary,
    dashboardTransactionsDynamicSummary,
    dashboardTransactionsSummary,
    dashboardMerchantsSummary,
    dashboardProvidersSummary,
    isSummaryAvailable,
    hasResults,
    hasErrors,
  } = useDashboardData(isEmptyUrlFilter ? null : filterValues);

  const summaryPeriod = getReportPeriod({
    dateGte: filterValues?.periodFrom,
    dateLte: filterValues?.periodTo,
    timezone: filterValues?.timezone || DEFAULT_TIMEZONE,
    withTimezone: false,
  });

  const renderContent = () => {
    if (isEmptyMerchantAccess) {
      return (
        <Alert severity="warning">
          {translate('app.common.emptyMerchantAccessAlert', {
            firstName: user.firstName,
            lastName: user.lastName,
          })}
        </Alert>
      );
    }

    if (!isSummaryAvailable && !hasErrors) {
      return <DashboardSkeleton />;
    }

    if ((!hasResults && !hasErrors) || isEmptyUrlFilter) {
      return <Alert severity="info">{translate('app.dashboard.noResultsAlert')}</Alert>;
    }

    return (
      <Stack gap="16px">
        <Alert severity="success">
          {translate('app.dashboard.timeRangeAlert', { timeRange: summaryPeriod })}
        </Alert>
        <BoxStyled>
          <DashboardTransactionsSummary
            summaryPeriod={summaryPeriod}
            transactionsSuccessSummary={dashboardTransactionsSuccessSummary}
            transactionsSummary={dashboardTransactionsSummary}
          />
          <DashboardTransactionsDynamicSummary
            error={dashboardTransactionsDynamicSummary?.error}
            isLoading={dashboardTransactionsDynamicSummary?.isLoading}
            summary={dashboardTransactionsDynamicSummary?.summary}
            summaryPeriod={summaryPeriod}
          />
          <DashboardMerchantsSummary
            error={dashboardMerchantsSummary?.error}
            isLoading={dashboardMerchantsSummary?.isLoading}
            summary={dashboardMerchantsSummary?.summary}
          />
          <DashboardPropvidersSummary
            error={dashboardProvidersSummary?.error}
            isLoading={dashboardProvidersSummary?.isLoading}
            summary={dashboardProvidersSummary?.summary}
          />
        </BoxStyled>
      </Stack>
    );
  };

  return (
    <ListContextProvider
      value={
        {
          filterValues,
          ...restContext,
        } as ListControllerResult
      }
    >
      <Box my={3}>
        <PageHeader
          headerTitle={{
            titleText: translate('app.dashboard.title'),
          }}
        />
        <DashboardFilter />
        {renderContent()}
      </Box>
    </ListContextProvider>
  );
};
